import { Component, OnInit, ViewChild } from '@angular/core';
import { features } from 'src/app/shared/helpers/tenant.features';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { SubscribeEmailsComponent } from 'src/app/shared/components/subscribe-emails/subscribe-emails.component';
import { Auth0Service } from 'src/app/auth/auth0.service';
import {
  UserProfile,
} from 'src/app/shared/components/personal-info/personal-info.component';
import { NewPassword } from '../../models/user';
import { NotificationService } from '../../services/notification.service';
import { UserService } from '../../services/user/user.service';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent implements OnInit {
  // Absent on white-label tenants and until the profile loads; Save then skips it.
  @ViewChild(SubscribeEmailsComponent) private subscriptions?: SubscribeEmailsComponent;
  loading: boolean;
  saving = false;
  readonly tenDigits = /^\d{10}$/;
  isBuyer: boolean;
  form: any;
  profile: any;
  isPasswordPopupOpen: boolean = false;

  new_password: NewPassword = { password: '', match_password: '' };
  profile_data: UserProfile = { id: null, phone: '', email: '' };
  readonly features = features;

  infoButtonOptions: any = {
    text: 'Update Info',
    type: 'success',
    useSubmitBehavior: true,
  };

  passwordButtonOptions: any = {
    text: 'Update Password',
    type: 'success',
    useSubmitBehavior: true,
  };

  constructor(
    private notificationService: NotificationService,
    private userService: UserService,
    private auth0: Auth0Service,
  ) {}

  ngOnInit() {
    this.loading = true;
    this.getMe();
  }

  getMe() {
    this.userService.getMe().subscribe(res => {
      this.profile = res;
      this.profile_data.id = this.profile.data.id;
      // Some saved phones keep their formatting, e.g. "(000) 000-0000"; the digits-only rule flags those on open.
      this.profile_data.phone = (this.profile.data.phone ?? '').replace(
        /\D/g,
        '',
      );
      this.profile_data.email = this.profile.data.email;
      this.isBuyer = this.profile.data.is_buyer;
      this.loading = false;
    });
  }

  onSave(e: Event): void {
    e.preventDefault();
    const saves = [this.labelledSave('Profile', this.userService.updateMe(this.profileUpdate()))];
    if (this.subscriptions) {
      saves.push(this.labelledSave('Email updates', this.subscriptions.save()));
    }
    this.saving = true;
    forkJoin(saves)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe(results =>
        this.reportSave(results.filter((failure): failure is string => failure !== null)),
      );
  }

  private profileUpdate() {
    return {
      id: this.profile.data.id,
      phone: this.profile_data.phone.replace(/\(*\)*\-* */gi, ''),
      email: this.profile_data.email,
    };
  }

  /** Null when the save worked, else "<label>: <error>", so one failure does not hide the other part. */
  private labelledSave(label: string, save$: Observable<unknown>): Observable<string | null> {
    return save$.pipe(
      map(() => null),
      catchError(error => of(`${label}: ${error}`)),
    );
  }

  private reportSave(failures: string[]): void {
    if (failures.length) {
      this.notificationService.showNotify(failures.join(' '), 'error');
      return;
    }
    this.notificationService.showNotify('Changes saved', 'success');
  }

  onPasswordSubmit(e) {
    this.userService.updatePassword(this.new_password).subscribe(
      () => {
        this.loading = false;
        this.notificationService.showNotify(
          'Password was updated, please log in.',
          'success',
        );
        this.auth0.logout();
      },
      error => {
        this.notificationService.showNotify(error, 'error');
        this.loading = false;
      },
    );
    e.preventDefault();
  }

  passwordComparison = () => {
    return this.form.instance.option('formData').password;
  };

  openUpdatePasswordPopup() {
    this.isPasswordPopupOpen = true;
  }

  closingPasswordPopup() {
    this.isPasswordPopupOpen = false;
  }
}
